import React, { useState, useEffect } from 'react';
import { Crown, Sparkles, Check, Rocket, Zap, Shield, CreditCard, Send, Loader2, Clock, TrendingUp, Target, Flame, BookOpen } from 'lucide-react';
import { BillingService } from '@/services/billingService';
import type { User } from '@supabase/supabase-js';
import { useTouchFeedback } from '@/hooks/useTouchFeedback';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  user?: User | null;
  onRefreshSubscription?: () => void;
  hasPendingRequest?: boolean;
}

export const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, isDark, user, onRefreshSubscription, hasPendingRequest }) => {
  const { triggerHaptic } = useTouchFeedback({ enableHaptics: true });
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly' | null>(null);
  const [trxId, setTrxId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'plans' | 'payment'>('plans');
  const [daysLeft, setDaysLeft] = useState(0);

  // Calculate days left in month
  useEffect(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const remaining = daysInMonth - now.getDate();
    setDaysLeft(Math.max(1, remaining));
  }, []);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setStep('plans');
      setSelectedPlan(null);
      setTrxId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePlanSelect = (plan: 'monthly' | 'yearly') => {
    triggerHaptic('medium');
    setSelectedPlan(plan);
    setStep('payment');
  };

  const handleSubmitRequest = async () => {
    if (!user || !user.email) {
      alert('Please sign in to upgrade.');
      return;
    }

    if (!selectedPlan) return;

    setIsSubmitting(true);
    const result = await BillingService.submitUpgradeRequest(user.email, selectedPlan, trxId);
    
    if (result.success) {
      alert('Upgrade request submitted successfully! An admin will review your payment and upgrade your account shortly.');
      onRefreshSubscription?.();
      onClose();
    } else {
      alert(`Error: ${result.error}`);
    }
    setIsSubmitting(false);
  };

  const plans = [
    {
      id: 'monthly' as const,
      name: 'Monthly Magic',
      price: '$5',
      originalPrice: '$20',
      discount: '75%',
      period: 'this month only',
      description: 'Limited giveaway pricing',
      popular: true,
      cta: 'Grab Before It Ends',
      savings: 'Save $15 this month',
    },
    {
      id: 'yearly' as const,
      name: 'Yearly Power',
      price: '$50',
      originalPrice: '$240',
      discount: '79%',
      period: 'entire year',
      description: 'Best long-term value',
      cta: 'Lock In Forever',
      savings: 'Save $190 yearly',
    }
  ];

  const features = [
    { icon: Zap, text: 'Unlimited Roadmaps - Create infinite learning paths' },
    { icon: Crown, text: 'All Expert Levels Unlocked' },
    { icon: Sparkles, text: 'AI Tutor Unlimited - Infinite AI assistance' },
    { icon: Shield, text: 'Streak Protection Guaranteed' },
    { icon: Rocket, text: 'Priority Support - 24/7 help' },
    { icon: Target, text: 'Advanced Analytics - Track your mastery' }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className={`relative w-full sm:max-w-2xl h-[98vh] sm:h-auto sm:max-h-[95vh] rounded-t-3xl sm:rounded-3xl overflow-hidden border-0 sm:border shadow-2xl transition-all flex flex-col ${
          isDark ? 'bg-gradient-to-b from-slate-900 to-slate-950 sm:border-white/10' : 'bg-gradient-to-b from-white to-slate-50 sm:border-zinc-200'
        }`}
      >
        {/* Urgent Banner */}
        <div className="relative py-3 sm:py-3.5 bg-gradient-to-r from-red-600 via-orange-600 to-red-600 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white to-transparent"></div>
          </div>
          <div className="relative text-center px-3 sm:px-4">
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-1">
              <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-200 animate-bounce" />
              <span className="text-white font-black text-xs sm:text-sm">🚨 URGENT GIVEAWAY</span>
              <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-200 animate-bounce" />
            </div>
            <p className="text-xs text-red-100 font-bold tracking-tight">
              {daysLeft} DAYS LEFT • LIMITED SLOTS
            </p>
          </div>
        </div>

        {/* Header */}
        <div className={`relative px-4 sm:px-6 py-3.5 sm:py-4 border-b ${
          isDark ? 'border-white/10 bg-slate-900/50' : 'border-red-100 bg-red-50/50'
        }`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-500 text-[10px] font-black mb-1.5 animate-pulse whitespace-nowrap">
                <span className="text-sm">⏰</span>
                THIS MONTH ONLY
              </div>
              <h2 className={`text-xl sm:text-2xl font-black leading-snug mb-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Don't Miss Out 🔥
              </h2>
              <p className={`text-xs sm:text-sm font-bold truncate ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                75% OFF Monthly
              </p>
            </div>
            <button 
              onClick={onClose}
              className={`flex-shrink-0 p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/5 text-zinc-400' : 'hover:bg-zinc-200 text-zinc-600'}`}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="px-3 sm:px-6 py-4 sm:py-5 space-y-3 sm:space-y-4">
            
            {hasPendingRequest ? (
              <div className={`p-5 sm:p-7 text-center rounded-2xl border-2 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-amber-500" />
                </div>
                <h3 className={`text-lg sm:text-xl font-bold mb-1.5 sm:mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  ✓ Upgrade Processing
                </h3>
                <p className={`text-xs sm:text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  We're verifying your payment. Your account will be upgraded within 1-2 hours.
                </p>
                <button 
                  onClick={onClose}
                  className="px-6 sm:px-7 py-2 sm:py-2.5 bg-gradient-to-r from-red-600 to-orange-600 text-white text-sm sm:text-base rounded-xl font-bold hover:shadow-lg transition-all w-full"
                >
                  Okay
                </button>
              </div>
            ) : step === 'plans' ? (
              <>
                {/* Plan Cards - URGENT DESIGN */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {plans.map((plan) => (
                    <button 
                      key={plan.id}
                      onClick={() => handlePlanSelect(plan.id as 'monthly' | 'yearly')}
                      onTouchStart={() => triggerHaptic('light')}
                      className={`relative p-4 sm:p-5 rounded-2xl border-2 transition-all group overflow-hidden touch-target card-touch active:scale-95 ${
                        selectedPlan === plan.id
                          ? `border-red-600 scale-105 ${isDark ? 'bg-gradient-to-br from-red-950/50 to-orange-950/50 shadow-xl shadow-red-500/30' : 'bg-gradient-to-br from-red-100 to-orange-50 shadow-xl shadow-red-500/20'}`
                          : isDark 
                            ? 'border-zinc-700 hover:border-red-500 hover:bg-slate-900/70' 
                            : 'border-red-200 hover:border-red-400 hover:bg-red-50'
                      }`}
                    >
                      {/* Background animation */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity bg-gradient-to-br from-red-500 via-orange-500 to-red-500"></div>

                      {plan.popular && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-600 to-orange-500 text-white text-[9px] sm:text-xs font-black px-3 py-1.5 rounded-full shadow-xl animate-pulse whitespace-nowrap">
                          ⭐ 75% OFF
                        </div>
                      )}

                      <div className="relative text-left pt-2">
                        {/* Savings Badge */}
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg mb-2 ${
                          isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'
                        } text-[9px] sm:text-xs font-bold`}>
                          <TrendingUp className="w-2.5 h-2.5" />
                          {plan.savings}
                        </div>

                        <div>
                          <div className={`text-sm font-black mb-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {plan.name}
                          </div>
                          
                          {/* Price with Original Crossed Out */}
                          <div className="flex items-baseline gap-1.5 mb-1">
                            <span className="text-3xl sm:text-3.5xl font-black bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">
                              {plan.price}
                            </span>
                            <span className={`text-sm line-through font-bold opacity-50 ${isDark ? 'text-zinc-400' : 'text-zinc-400'}`}>
                              {plan.originalPrice}
                            </span>
                            <span className={`text-xs font-black px-1.5 py-0.5 rounded ${isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                              -{plan.discount}
                            </span>
                          </div>

                          <p className={`text-[10px] sm:text-xs font-bold mb-1.5 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                            {plan.period}
                          </p>
                          <p className={`text-[10px] sm:text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                            {plan.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Urgency Message */}
                <div className={`p-3 rounded-xl border-2 border-dashed ${isDark ? 'bg-red-950/30 border-red-500/30' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-start gap-2">
                    <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 animate-pulse mt-0.5" />
                    <div className="min-w-0">
                      <p className={`text-xs font-bold leading-tight ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                        ⚠️ Limited Slots Available
                      </p>
                      <p className={`text-[10px] leading-tight ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        Ends in {daysLeft} days
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features Grid */}
                <div className="space-y-2">
                  <h3 className={`font-bold text-xs sm:text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Unlock Unlimited Access:
                  </h3>
                  <div className="grid grid-cols-1 gap-1.5">
                    {features.map((feature, i) => (
                      <div key={i} className={`flex items-start gap-2 p-2 sm:p-2.5 rounded-lg ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-100 hover:bg-slate-200'} transition-colors`}>
                        <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mt-0.5">
                          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                        <span className={`text-xs sm:text-sm font-medium leading-snug ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button - BIG and URGENT */}
                <button
                  onClick={() => setStep('payment')}
                  disabled={!selectedPlan}
                  className={`w-full px-5 py-3 sm:py-4 bg-gradient-to-r from-red-600 via-orange-600 to-red-600 text-white font-black rounded-2xl hover:shadow-2xl hover:shadow-red-500/40 transition-all transform hover:scale-105 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                  <span className="relative flex items-center justify-center gap-2">
                    🔥 {selectedPlan === 'monthly' ? 'SAVE $15 NOW' : 'SAVE $190 NOW'} 🔥
                  </span>
                </button>

                {/* Risk Reversal */}
                <p className={`text-center text-[10px] sm:text-xs font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  ✓ 100% Risk-Free · Cancel Anytime · Instant Access · No Questions Asked
                </p>
              </>
            ) : (
              <>
                <button 
                  onClick={() => setStep('plans')}
                  className={`flex items-center gap-2 text-xs sm:text-sm font-bold hover:underline mb-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                >
                  ← Back to Plans
                </button>

                {/* Selected Plan Card */}
                <div className={`p-4 sm:p-6 rounded-2xl border-2 border-dashed bg-gradient-to-br ${isDark ? 'from-red-950/40 to-orange-950/40 border-red-500/50' : 'from-red-50 to-orange-50 border-red-300'}`}>
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h4 className={`font-black text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      ✓ Plan Selected
                    </h4>
                    <div className={`px-3 py-1 rounded-lg text-[10px] sm:text-xs font-black ${isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                      {selectedPlan === 'monthly' ? '💰 $5/MONTH (75% OFF)' : '💰 $50/YEAR (79% OFF)'}
                    </div>
                  </div>
                  <p className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                    {selectedPlan === 'monthly' 
                      ? `🎁 Special giveaway pricing - only $5 this month (normally $20)`
                      : `🎁 Best annual value - only $50/year (normally $240)`}
                  </p>
                </div>

                {/* Payment Instructions */}
                <div className={`space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-2xl border-2 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                  <h4 className={`font-bold text-sm sm:text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <CreditCard className="w-5 h-5" />
                    Payment Instructions
                  </h4>
                  
                  <div className="space-y-3 text-xs sm:text-sm">
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-lg font-bold text-xs sm:text-sm flex items-center justify-center ${isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                        1
                      </span>
                      <div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Send Payment</p>
                        <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                          Send {selectedPlan === 'monthly' ? '$5' : '$50'} to: <span className="font-mono font-bold">support@nanolezdubai.com</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-lg font-bold text-xs sm:text-sm flex items-center justify-center ${isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                        2
                      </span>
                      <div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Share Transaction ID</p>
                        <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                          Copy your payment reference/transaction ID below
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-lg font-bold text-xs sm:text-sm flex items-center justify-center ${isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                        3
                      </span>
                      <div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Get Verified</p>
                        <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                          We'll verify and upgrade you within 1-2 hours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TRX ID Input */}
                <div>
                  <label className={`block text-xs sm:text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Transaction ID
                  </label>
                  <input 
                    type="text"
                    value={trxId}
                    onChange={(e) => setTrxId(e.target.value)}
                    placeholder="Paste your transaction/reference ID here"
                    className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500 focus:border-red-500 focus:bg-white/10' 
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-red-500 focus:bg-red-50'
                    } focus:outline-none`}
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmitRequest}
                  disabled={isSubmitting || !trxId.trim()}
                  className={`w-full px-6 py-4 sm:py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-black rounded-2xl hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Complete Upgrade
                    </>
                  )}
                </button>

                <p className={`text-center text-[10px] sm:text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  ✓ Secure payment processing · Your data is encrypted
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
